import React, { useCallback } from "react";
import { graphql } from "react-relay";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";

import type { ColorPickerMutation, EventEditType } from "~/__generated__/ColorPickerMutation.graphql";
import type { fellow_color as EventColor } from "~/__generated__/useSaveEventMutationMutation.graphql";

import styles from "./ColorPicker.module.css";

export type { EventColor };

const FELLOW_COLORS: EventColor[] = ["RED", "PEACH", "YELLOW", "GREEN", "ROYAL", "PURPLE", "GREY"];
const FELLOW_COLOR_HEXES = ["#e05a5b", "#f09656", "#f7ba02", "#25b878", "#2673EE", "#9178fa", "#6a7980"];

const colorPickerMutation = graphql`
	mutation ColorPickerMutation($calendarId: ID!, $guid: String!, $eventColor: EventColor!, $editType: EventEditType!) {
		changeEventColor(calendarId: $calendarId, guid: $guid, eventColor: $eventColor, editType: $editType) {
			event {
				id
				guid
				color
				fellowColor
			}
		}
	}
`;

export function useChangeEventColorMutation(setEventToLoading: (eventGuid: string) => () => void) {
	return useCallback(
		async ({
			calendarId,
			guid,
			newColor,
			editType,
		}: {
			calendarId: string;
			guid: string;
			newColor: EventColor;
			editType: EventEditType;
		}) => {
			const stopLoading = setEventToLoading(guid);
			const response = await commitMutationPromise<ColorPickerMutation>(environment, {
				mutation: colorPickerMutation,
				variables: {
					calendarId,
					guid,
					eventColor: newColor,
					editType,
				},
			});

			stopLoading();
			const result = response.changeEventColor;

			if (!result || !result.event) {
				return {
					name: "bad-return-format" as const,
					message: "Could not confirm that the event color was changed.",
				};
			}

			return result.event;
		},
		[setEventToLoading],
	);
}

function ColorButton({ color, selected, onClick }: { color: string; selected: boolean; onClick?: () => void }) {
	return (
		<button
			className={cx(styles.colorButton, { [styles.selected]: selected })}
			style={{ backgroundColor: color }}
			onClick={onClick}
		>
			{selected && <FontAwesomeIcon icon={faCheck} className={styles.selectionCheck} />}
		</button>
	);
}

interface ColorPickerProps {
	calendarColor: string | null;
	providerEventColor: string | null;
	selectedColor: EventColor;
	onSelectColor: (color: EventColor) => void;
}

export default function ColorPicker({
	calendarColor,
	providerEventColor,
	selectedColor,
	onSelectColor,
}: ColorPickerProps) {
	const selectColorFn = React.useCallback((color: EventColor) => () => onSelectColor(color), [onSelectColor]);

	return (
		<div className={styles.pickerRow}>
			<ColorButton
				color={calendarColor || "var(--vividRoyal)"}
				selected={selectedColor === "CALENDAR"}
				onClick={selectColorFn("CALENDAR")}
			/>
			{providerEventColor && !FELLOW_COLOR_HEXES.includes(providerEventColor) && (
				<ColorButton
					color={providerEventColor}
					selected={selectedColor === "PROVIDER"}
					onClick={selectColorFn("PROVIDER")}
				/>
			)}
			<span className={styles.separator} />
			{FELLOW_COLORS.map(color => (
				<ColorButton
					key={color}
					color={`var(--event-${color.toLocaleLowerCase()})`}
					selected={selectedColor === color}
					onClick={selectColorFn(color)}
				/>
			))}
		</div>
	);
}
